import React from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import {ArrowBack} from '@mui/icons-material';
import Loading from '$components/Loading';
import {usePaymentDetails} from '../../Finance/PaymentDetails/hooks';
import {usePaymentMethodTypesHook} from '../hooks/usePaymentMethodTypesHook';
import {
  containerStyles,
  contentWrapperStyles,
  backButtonStyles,
  verticalDividerStyles,
  headerTitleStyles,
  bookingPaymentTitleStyles,
  actionButtonsContainerStyles,
  dropdownFormControlStyles,
  dropdownLabelTypographyStyles,
  modalBoxStyles,
  modalTitleTypographyStyles,
  modalDescriptionTypographyStyles,
  modalActionsBoxStyles,
  subtitleTypographyStyles,
  invoiceDetailsBoxStyles,
  paymentMethodBoxStyles,
} from './styles';
import PaymentSummary from '../../common/components/PaymentSummary';
import PaymentSummaryBox from '$modules/booking/components/PaymentSummaryBox';
import GenerateInvoiceButton from '../../common/components/GenerateInvoiceButton';
import {BOOKING_PAYMENT_DETAILS_STRINGS, PAYMENT_STATUS_MAP} from './constant';
import FrontdeskButton from '$components/FrontdeskButton';
import DialogComponent from '$components/DialogComponent';
import {useUpdatePaymentHook} from '../hooks/useUpdatePaymentHook';
import {RouteSection, getRoute} from '$utils/route';
import {RoutePath} from '$constants/routes';
import PaymentLinkModal from '../../Finance/PaymentDetails/PaymentLinkModal';
import {useQueryClient} from '@tanstack/react-query';

const BookingPaymentDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const backRoute = location?.state?.backRoute;

  const {id} = useParams<{id: string}>();
  const {payment, isLoading, isError, message} = usePaymentDetails(id || '');
  const [selectedPaymentMode, setSelectedPaymentMode] = React.useState('');
  const {
    data: paymentModes,
    isLoading: isPaymentModesLoading,
    isError: isPaymentModesError,
  } = usePaymentMethodTypesHook();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [paymentLinkModalOpen, setPaymentLinkModalOpen] = React.useState(false);
  const paymentId = payment?.id;
  const queryClient = useQueryClient();

  const updatePaymentMutation = useUpdatePaymentHook();

  const handleBackClick = () => {
    const route = backRoute || getRoute(RouteSection.Calendar);
    navigate(route);
  };

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !payment) {
    return (
      <div>{message || BOOKING_PAYMENT_DETAILS_STRINGS.noPaymentDetails}</div>
    );
  }
  const {title = '', subtitle = ''} =
    PAYMENT_STATUS_MAP[payment.status as keyof typeof PAYMENT_STATUS_MAP] || {};

  const SummaryBoxitems = {
    label: BOOKING_PAYMENT_DETAILS_STRINGS.consultationLabel,
    amount: Number(payment.amount) || 0,
  };

  const handlePaymentMethodChange = (value: string) => {
    setSelectedPaymentMode(value);
    const selectedPaymentMethod = paymentModes?.data?.find(
      opt => opt.id === value
    );

    if (
      selectedPaymentMethod?.id === 'payment_link' ||
      selectedPaymentMethod?.name?.toLowerCase().includes('payment link')
    ) {
      setPaymentLinkModalOpen(true);
      setModalOpen(false);
    }
  };

  const handleTakePayment = () => {
    if (!selectedPaymentMode) return;
    updatePaymentMutation.mutate(
      {
        payment_id: id || '',
        payment_method_type: selectedPaymentMode,
      },
      {
        onSuccess: () => {
          handlePaymentSuccess();
        },
      }
    );
  };

  const handlePaymentLinkSend = (data: {
    sendViaEmail: boolean;
    sendViaSms: boolean;
    email: string;
    phone: string;
  }) => {
    updatePaymentMutation.mutate(
      {
        payment_id: id || '',
        payment_method_type: selectedPaymentMode,
        email: data.email,
        phone: data.phone,
        send_via_email: data.sendViaEmail,
        send_via_sms: data.sendViaSms,
      },
      {
        onSuccess: () => {
          setPaymentLinkModalOpen(false);
          setModalOpen(false);
          queryClient.invalidateQueries({queryKey: ['payment-details', id]});
        },
      }
    );
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedPaymentMode('');
  };

  const handlePaymentLinkModalClose = () => {
    setPaymentLinkModalOpen(false);
    setSelectedPaymentMode('');
  };

  const handlePaymentSuccess = () => {
    navigate(
      `${getRoute(RouteSection.Calendar, RoutePath.staff.calendar.bookingDetails)}/payment-success`,
      {
        state: {
          paymentId,
        },
      }
    );
  };

  return (
    <Box sx={containerStyles}>
      <Box sx={contentWrapperStyles}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackClick}
          sx={backButtonStyles}
        >
          {BOOKING_PAYMENT_DETAILS_STRINGS.back}
        </Button>
        <Divider sx={verticalDividerStyles} />
        <Box>
          <Typography variant="h5" sx={bookingPaymentTitleStyles}>
            {title}
          </Typography>
          <Typography variant="subtitle1" sx={subtitleTypographyStyles}>
            {subtitle}
          </Typography>
        </Box>
        <Box sx={invoiceDetailsBoxStyles}>
          <Typography sx={headerTitleStyles}>
            {BOOKING_PAYMENT_DETAILS_STRINGS.invoiceDetails}
          </Typography>
          <Divider sx={verticalDividerStyles} />
          <PaymentSummary
            amount={payment.amount}
            status={payment.status}
            lastUpdate={payment.billing_date}
            paymentMethodType={payment.payment_method || '-'}
            invoiceNumber={payment.invoice_number}
            dateLabel="Billing Date"
            paymentLink={payment.payment_link}
          />
        </Box>

        <PaymentSummaryBox
          totalAmount={Number(payment.amount) || 0}
          items={[SummaryBoxitems]}
          currency={'£'}
          headerText={
            payment.status === 'paid'
              ? BOOKING_PAYMENT_DETAILS_STRINGS.totalPayment
              : BOOKING_PAYMENT_DETAILS_STRINGS.totalToPay
          }
          width="50%"
        />
        {payment.status === 'unpaid' && (
          <Box sx={paymentMethodBoxStyles}>
            <Typography variant="subtitle2" sx={dropdownLabelTypographyStyles}>
              {BOOKING_PAYMENT_DETAILS_STRINGS.selectPaymentMethodLabel}
            </Typography>
            <FormControl sx={dropdownFormControlStyles}>
              <InputLabel id="payment-mode-label">
                {BOOKING_PAYMENT_DETAILS_STRINGS.paymentLinkLabel}
              </InputLabel>
              <Select
                labelId="payment-mode-label"
                value={selectedPaymentMode}
                label={BOOKING_PAYMENT_DETAILS_STRINGS.selectPaymentMethodLabel}
                onChange={e => handlePaymentMethodChange(e.target.value)}
                disabled={isPaymentModesLoading || isPaymentModesError}
              >
                {isPaymentModesLoading && (
                  <MenuItem value="">
                    {BOOKING_PAYMENT_DETAILS_STRINGS.loadingPaymentMethods}
                  </MenuItem>
                )}
                {isPaymentModesError && (
                  <MenuItem value="">
                    {BOOKING_PAYMENT_DETAILS_STRINGS.errorLoadingPaymentMethods}
                  </MenuItem>
                )}
                {paymentModes?.data?.map(option => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      <Box sx={actionButtonsContainerStyles}>
        <GenerateInvoiceButton
          paymentId={id}
          payment={payment}
          context="calendar"
        />
        {payment.status === 'unpaid' && (
          <FrontdeskButton
            title={BOOKING_PAYMENT_DETAILS_STRINGS.takePayment}
            className="take-payment-btn--booking-payment-details"
            onClick={() => setModalOpen(true)}
            disabled={!selectedPaymentMode}
          />
        )}
      </Box>
      <DialogComponent
        open={modalOpen}
        handleClose={handleModalClose}
        dialogBody={
          <Box sx={modalBoxStyles}>
            <Typography variant="h6" sx={modalTitleTypographyStyles}>
              {BOOKING_PAYMENT_DETAILS_STRINGS.confirmPaymentTitle}
            </Typography>
            <Typography sx={modalDescriptionTypographyStyles}>
              {BOOKING_PAYMENT_DETAILS_STRINGS.confirmPaymentMessage}
            </Typography>
            <Typography sx={modalDescriptionTypographyStyles}>
              {BOOKING_PAYMENT_DETAILS_STRINGS.paymentMethodLabel}:{' '}
              <b>
                {paymentModes?.data?.find(opt => opt.id === selectedPaymentMode)
                  ?.name || ''}
              </b>
            </Typography>
            <Box sx={modalActionsBoxStyles}>
              <FrontdeskButton
                title={BOOKING_PAYMENT_DETAILS_STRINGS.cancel}
                variant="secondary"
                className="cancel-red"
                onClick={handleModalClose}
              />
              <FrontdeskButton
                title={BOOKING_PAYMENT_DETAILS_STRINGS.confirm}
                variant="primary"
                onClick={handleTakePayment}
                loading={updatePaymentMutation.isPending}
                disabled={
                  !selectedPaymentMode || updatePaymentMutation.isPending
                }
              />
            </Box>
          </Box>
        }
      />

      <PaymentLinkModal
        open={paymentLinkModalOpen}
        onClose={handlePaymentLinkModalClose}
        onSend={handlePaymentLinkSend}
        paymentLink={payment?.payment_link || ''}
        email={payment?.email}
        phone={payment?.phone}
        countryCode={payment?.country_code}
        isLoading={updatePaymentMutation.isPending}
      />
    </Box>
  );
};

export default BookingPaymentDetails;
